import { __awaiter, __decorate } from "tslib";
import { Auth } from '@genesislcap/foundation-comms';
import { customElement } from '@microsoft/fast-element';
import { EntityManagement, styles, template } from '../entities';
import { editProfileFormSchema } from './editProfileSchema';
/**
 * Main class which defined the profile management functionality
 *
 * @remarks
 *
 * Under the hood the profile-management is the entity-management micro-frontend configured for use
 * with the `ALL_PROFILES` resource.
 *
 * @public
 *
 * @example
 *
 * You can use this class by forward declaring it in the template file where it is used, and then using
 * it in a html template. It is recommended to wrap it in an error boundary.
 * ```javascript
 * <profile-management></profile-management>
 * ```
 *
 */
let Profiles = class Profiles extends EntityManagement {
    /**
     * Constructor will set the {@link EntityManagement.createEvent} {@link EntityManagement.deleteEvent} {@link EntityManagement.updateEvent} to match the permissions set from the server
     */
    constructor() {
        super();
        this.createEvent = this.auth.currentUser.hasPermission('INSERT_PROFILE')
            ? 'EVENT_INSERT_PROFILE'
            : '';
        this.deleteEvent = this.auth.currentUser.hasPermission('DELETE_PROFILE')
            ? 'EVENT_DELETE_PROFILE'
            : '';
        this.updateEvent = this.auth.currentUser.hasPermission('AMEND_PROFILE')
            ? 'EVENT_AMEND_PROFILE'
            : '';
        this.persistColumnStateKey = 'entity_profiles_management';
        this.updateFormUiSchema = editProfileFormSchema;
        this.createFormUiSchema = editProfileFormSchema;
        this.readEventFn = this.readProfileData.bind(this);
        this.resourceName = 'ALL_PROFILES';
        this.title = 'Profile Management';
        this.entityLabel = 'Profile';
    }
    readProfileData(profile) {
        return __awaiter(this, void 0, void 0, function* () {
            const selectedRightsReq = yield this.connect.request('PROFILE_RIGHT', {
                REQUEST: {
                    PROFILE_NAME: profile.PROFILE_NAME,
                    RIGHT_CODE: '*',
                },
            });
            const selectedUsersReq = yield this.connect.request('PROFILE_USER', {
                REQUEST: {
                    PROFILE_NAME: profile.PROFILE_NAME,
                    USER_NAME: '*',
                },
            });
            return {
                RIGHT_CODES: selectedRightsReq.REPLY.map((v) => v.RIGHT_CODE),
                USER_NAMES: Array.from(new Set(selectedUsersReq.REPLY.map((v) => v.USER_NAME))),
                NAME: profile.PROFILE_NAME,
            };
        });
    }
    confirmDelete() {
        return __awaiter(this, void 0, void 0, function* () {
            const deleteReq = yield this.connect.commitEvent(this.deleteEvent, {
                DETAILS: { NAME: this.selectedEntity.PROFILE_NAME },
            });
            this.errorNotify(deleteReq);
        });
    }
};
__decorate([
    Auth
], Profiles.prototype, "auth", void 0);
Profiles = __decorate([
    customElement({
        name: 'profile-management',
        template,
        styles,
    })
], Profiles);
export { Profiles };
